import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import type { InspectorDTO } from 'src/types/dto/inspector.dto';
import type { RescheduleChangeDTO } from 'src/types/dto/reschedule-suggestion.dto';

import dayjs from 'dayjs';

import type { Tag } from 'src/components/tags-list';
import { appointmentDisplayDurationDays } from 'src/utils/appointments';
import { roundDuration } from 'src/utils/duration';
import { fFormatDuration } from 'src/utils/format-time';
import { competencyIdsFromActivity } from 'src/sections/schedule/utils/planbox-activity-selection';

export const getActivityCompetencyIds = (activity?: ActivityDTO) =>
  competencyIdsFromActivity(activity);

export const getInspectorAuthorizationsForActivity = (
  inspector?: InspectorDTO,
  activityCompetencyIds?: number[]
): AuthorizationDTO[] => {
  const authorizations = inspector?.authorizations ?? [];
  if (!activityCompetencyIds?.length) {
    return [];
  }

  return authorizations.filter((authorization) => {
    const competencyId = authorization.competency?.id;
    return (
      typeof competencyId === 'number' && activityCompetencyIds.includes(competencyId)
    );
  });
};

export const getActivityCompetencyTags = (activity?: ActivityDTO): Tag[] =>
  activity?.activityCompetencies?.flatMap((item) => {
    const id = item.competency?.id;
    const label = item.competency?.name;

    if (id == null || !label) {
      return [];
    }

    return [{ id, label, color: 'info' as const, variant: 'filled' as const }];
  }) ?? [];

export const getActivityCurrentAppointments = (activity?: ActivityDTO) =>
  activity?.appointments ?? [];

export const getEarliestNonBlockerAppointment = (appointments: AppointmentDTO[] = []) =>
  appointments
    .filter((appointment) => appointment.type !== 'blocker')
    .sort((left, right) => dayjs(left.from).valueOf() - dayjs(right.from).valueOf())[0];

export const getNonBlockerAppointments = (appointments: AppointmentDTO[] = []) =>
  appointments.filter((appointment) => appointment.type !== 'blocker');

export const getSortedNonBlockerAppointments = (appointments: AppointmentDTO[] = []) =>
  getNonBlockerAppointments(appointments).sort(
    (left, right) => dayjs(left.from).valueOf() - dayjs(right.from).valueOf()
  );

export const getInspectorBusinessUnit = (inspector?: InspectorDTO) =>
  inspector?.businessUnit?.name ?? inspector?.user?.businessUnits?.[0]?.name ?? '-';

export const getInspectorLocation = (inspector?: InspectorDTO) => {
  if (inspector?.address) {
    return inspector.address;
  }

  return [inspector?.postalCode, inspector?.city, inspector?.country].filter(Boolean).join(', ') || '-';
};

export const getAppointmentDays = (appointments: AppointmentDTO[] = []) =>
  roundDuration(
    getNonBlockerAppointments(appointments).reduce(
      (total, appointment) => total + appointmentDisplayDurationDays(appointment),
      0
    )
  );

export const getActivityDisplayDurationDays = (
  activity?: ActivityDTO,
  appointments: AppointmentDTO[] = []
) => {
  if (activity?.durationDays != null && Number.isFinite(Number(activity.durationDays))) {
    return roundDuration(Number(activity.durationDays));
  }

  return getAppointmentDays(appointments);
};

export const getTravelSummary = (appointments: AppointmentDTO[] = [], isDeskService = false) => {
  if (isDeskService) {
    return { durationLabel: '0.0km', distanceLabel: undefined };
  }

  const travelTimeSeconds = appointments.reduce(
    (total, appointment) => total + (appointment.travelTime ?? 0),
    0
  );
  const travelDistance = appointments.reduce(
    (total, appointment) =>
      total +
      +(appointment.travelToSiteDistance ?? 0) +
      +(appointment.travelFromSiteDistance ?? 0),
    0
  );

  return {
    durationLabel:
      travelTimeSeconds > 0
        ? fFormatDuration(travelTimeSeconds)
        : travelDistance > 0
          ? `${travelDistance.toFixed(1)}km`
          : '-',
    distanceLabel:
      travelTimeSeconds > 0 && travelDistance > 0 ? `${travelDistance.toFixed(0)}km` : undefined,
  };
};

export const getEarliestNewAppointment = (changes: RescheduleChangeDTO[]) => {
  const appointments = changes.flatMap((change) => change.newAppointment);
  return appointments.filter(appointment => appointment.type !== 'blocker').sort((left, right) => dayjs(left.from).valueOf() - dayjs(right.from).valueOf())[0];
};

export const getMainActivityChange = (
  changes: RescheduleChangeDTO[],
  currentActivityId: number
) => changes.find((change) => change.activity.id === currentActivityId) ?? changes[0];

export const getMainActivityProposedAppointment = (
  changes: RescheduleChangeDTO[],
  currentActivityId: number
) => {
  const mainChange = getMainActivityChange(changes, currentActivityId);
  if (!mainChange) {
    return undefined;
  }

  return getEarliestNonBlockerAppointment(mainChange.newAppointment);
};

export const getTotalNewAppointmentDays = (
  changes: RescheduleChangeDTO[],
  currentActivityId: number
) => {
  const mainChange = getMainActivityChange(changes, currentActivityId);
  if (!mainChange) {
    return 0;
  }

  return getActivityDisplayDurationDays(mainChange.activity, mainChange.newAppointment);
};

export const getAggregatedNewAppointments = (changes: RescheduleChangeDTO[]) =>
  changes.flatMap((change) => change.newAppointment).filter(appointment => appointment.type !== 'blocker');

export const isSameTeamSuggestion = (changes: RescheduleChangeDTO[], inspector: InspectorDTO) => {
  const proposedBusinessUnit = getInspectorBusinessUnit(inspector);
  if (!proposedBusinessUnit || proposedBusinessUnit === '-') {
    return false;
  }

  return changes.some((change) => {
    const currentInspector = getActivityCurrentAppointments(change.activity)[0]?.inspector;
    return getInspectorBusinessUnit(currentInspector) === proposedBusinessUnit;
  });
};

export const formatRescheduleCardDate = (value?: string | Date) => {
  if (!value) {
    return '-';
  }

  const date = dayjs(value);
  return date.isValid() ? date.format('ddd, DD MMM YY') : '-';
};

export const formatRescheduleDetailDate = (value?: string | Date, siteTimezoneId?: string) => {
  if (!value) {
    return '-';
  }

  const date = dayjs.tz(value, siteTimezoneId);
  return date.isValid() ? date.format('DD MMM YY') : '-';
};
